import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { useTheme } from '../../contexts/ThemeContext';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import {
  usePodcastRegistry, liveEpisodesNewestFirst, formatDuration,
} from './podcastShow';
import { generatePodcastHubSchema } from './podcastSchema';
import { getSocialCounts, toggleEpisodeLike } from '../../services/podcastApi';
import PodcastPlayer from './PodcastPlayer';
import './Podcast.css';

/**
 * PodcastHubPage — /podcast (CEL-1355; design pass = Direction C "The
 * Interview", Nitish's pick shift 102): the latest episode's PULL QUOTE leads
 * the page as the hero, episodes render as full-width entries (the featured
 * one carrying the inline player — PRD §2.2's playable-latest requirement),
 * subscribe row (null links don't render), and the be-a-guest intake.
 * Episode-count aware: presentable at 1 and at 50. Live entries only (D4).
 */
/* Platform glyphs: official Simple Icons vector paths in each brand's color
   (never image-generated, per the brand-kit rule). Rendered inside the same
   pod-pill next to the label, mirroring the ICON_PLAY pattern. */
const subIcon = (path, color) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d={path} />
  </svg>
);

const SUBSCRIBE_APPS = [
  {
    key: 'apple',
    label: 'Apple Podcasts',
    icon: subIcon(
      'M5.34 0A5.328 5.328 0 000 5.34v13.32A5.328 5.328 0 005.34 24h13.32A5.328 5.328 0 0024 18.66V5.34A5.328 5.328 0 0018.66 0zm6.525 2.568c2.336 0 4.448.902 6.056 2.587 1.224 1.272 1.912 2.619 2.264 4.392.12.59.12 2.2.007 2.864a8.506 8.506 0 01-3.24 5.296c-.608.46-2.096 1.261-2.336 1.261-.088 0-.096-.091-.056-.46.072-.592.144-.715.48-.856.536-.224 1.448-.874 2.008-1.435a7.644 7.644 0 002.008-3.536c.208-.824.184-2.656-.048-3.504-.728-2.696-2.928-4.792-5.624-5.352-.784-.16-2.208-.16-3 0-2.728.56-4.984 2.76-5.672 5.528-.184.752-.184 2.584 0 3.336.456 1.832 1.64 3.512 3.192 4.512.304.2.672.408.824.472.336.144.408.264.472.856.04.36.03.464-.056.464-.056 0-.464-.176-.896-.384l-.04-.03c-2.472-1.216-4.056-3.274-4.632-6.012-.144-.706-.168-2.392-.03-3.04.36-1.74 1.048-3.1 2.192-4.304 1.648-1.737 3.768-2.656 6.128-2.656zm.134 2.81c.409.004.803.04 1.106.106 2.784.62 4.76 3.408 4.376 6.174-.152 1.114-.536 2.03-1.216 2.88-.336.43-1.152 1.15-1.296 1.15-.023 0-.048-.272-.048-.603v-.605l.416-.496c1.568-1.878 1.456-4.502-.256-6.224-.664-.67-1.432-1.064-2.424-1.246-.64-.118-.776-.118-1.448-.008-1.02.167-1.81.562-2.512 1.256-1.72 1.704-1.832 4.342-.264 6.222l.413.496v.608c0 .336-.027.608-.06.608-.03 0-.264-.16-.512-.36l-.034-.011c-.832-.664-1.568-1.842-1.872-2.997-.184-.698-.184-2.024.008-2.72.504-1.878 1.888-3.335 3.808-4.019.41-.145 1.133-.22 1.814-.211zm-.13 2.99c.31 0 .62.06.844.178.488.253.888.745 1.04 1.259.464 1.578-1.208 2.96-2.72 2.254h-.015c-.712-.331-1.096-.956-1.104-1.77 0-.733.408-1.371 1.112-1.745.224-.117.534-.176.844-.176zm-.011 4.728c.988-.004 1.706.349 1.97.97.198.464.124 1.932-.218 4.302-.232 1.656-.36 2.074-.68 2.356-.44.39-1.064.498-1.656.288h-.003c-.716-.257-.87-.605-1.164-2.644-.341-2.37-.416-3.838-.218-4.302.262-.616.974-.966 1.97-.97z',
      '#9933CC'
    ),
  },
  {
    key: 'spotify',
    label: 'Spotify',
    icon: subIcon(
      'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z',
      '#1DB954'
    ),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    icon: subIcon(
      'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
      '#FF0000'
    ),
  },
  {
    key: 'amazon',
    label: 'Amazon Music',
    icon: subIcon(
      'M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-10.951-.577 17.88 17.88 0 01-5.43-3.35c-.1-.074-.151-.15-.151-.22 0-.047.021-.09.051-.13zm6.565-6.218c0-1.005.247-1.863.743-2.577.495-.71 1.17-1.25 2.04-1.615.796-.335 1.756-.575 2.912-.72.39-.046 1.033-.103 1.92-.174v-.37c0-.93-.105-1.558-.3-1.875-.302-.43-.78-.65-1.44-.65h-.182c-.48.046-.896.196-1.246.46-.35.27-.575.63-.675 1.096-.06.3-.206.465-.435.51l-2.52-.315c-.248-.06-.372-.18-.372-.39 0-.046.007-.09.022-.15.247-1.29.855-2.25 1.82-2.88.976-.616 2.1-.975 3.39-1.05h.54c1.65 0 2.957.434 3.888 1.29.135.15.27.3.405.48.12.165.224.314.283.45.075.134.15.33.195.57.06.254.105.42.135.51.03.104.062.3.076.615.01.313.02.493.02.553v5.28c0 .376.06.72.165 1.036.105.313.21.54.315.674l.51.674c.09.136.136.256.136.36 0 .12-.06.226-.18.314-1.2 1.05-1.86 1.62-1.963 1.71-.165.135-.375.15-.63.045a6.062 6.062 0 01-.526-.496l-.31-.347a9.391 9.391 0 01-.317-.42l-.3-.435c-.81.886-1.603 1.44-2.4 1.665-.494.15-1.093.227-1.83.227-1.11 0-2.04-.343-2.76-1.034-.72-.69-1.08-1.665-1.08-2.94l-.05-.076zm3.753-.438c0 .566.14 1.02.425 1.364.285.34.675.512 1.155.512.045 0 .106-.007.195-.02.09-.016.134-.023.166-.023.614-.16 1.08-.553 1.424-1.178.165-.28.285-.58.36-.91.09-.32.12-.59.135-.8.015-.195.015-.54.015-1.005v-.54c-.84 0-1.484.06-1.92.18-1.275.36-1.92 1.17-1.92 2.43l-.035-.02zm9.162 7.027c.03-.06.075-.11.132-.17.362-.243.714-.41 1.05-.5a8.094 8.094 0 011.612-.24c.14-.012.28 0 .41.03.65.06 1.05.168 1.172.33.063.09.099.228.099.39v.15c0 .51-.149 1.11-.424 1.8-.278.69-.664 1.248-1.156 1.68-.073.06-.14.09-.197.09-.03 0-.06 0-.09-.012-.09-.044-.107-.12-.064-.24.54-1.26.806-2.143.806-2.64 0-.15-.03-.27-.087-.344-.145-.166-.55-.257-1.224-.257-.243 0-.533.016-.87.046-.363.045-.7.09-1 .135-.09 0-.148-.014-.18-.044-.03-.03-.036-.047-.02-.077 0-.017.006-.03.02-.063v-.06z',
      '#FF9900'
    ),
  },
  {
    key: 'deezer',
    label: 'Deezer',
    icon: subIcon(
      'M.693 10.024c.381 0 .693-1.256.693-2.807 0-1.55-.312-2.807-.693-2.807C.312 4.41 0 5.666 0 7.217s.312 2.808.693 2.808ZM21.038 1.56c-.364 0-.684.805-.91 2.096C19.765 1.446 19.184 0 18.526 0c-.78 0-1.464 2.036-1.784 5-.312-2.158-.788-3.536-1.325-3.536-.745 0-1.386 2.704-1.62 6.472-.442-1.932-1.083-3.145-1.793-3.145s-1.35 1.213-1.793 3.145c-.242-3.76-.874-6.463-1.628-6.463-.537 0-1.013 1.378-1.325 3.535C6.938 2.036 6.262 0 5.474 0c-.658 0-1.247 1.447-1.602 3.665-.217-1.291-.546-2.105-.91-2.105-.675 0-1.221 2.807-1.221 6.272 0 3.466.546 6.273 1.221 6.273.277 0 .537-.476.736-1.273.32 2.928.996 4.938 1.776 4.938.606 0 1.143-1.204 1.507-3.11.251 3.622.875 6.195 1.602 6.195.46 0 .875-1.023 1.187-2.677C10.142 21.6 11 24 12.004 24c1.005 0 1.863-2.4 2.235-5.822.312 1.654.727 2.677 1.186 2.677.728 0 1.352-2.573 1.603-6.195.364 1.906.9 3.11 1.507 3.11.78 0 1.455-2.01 1.775-4.938.208.797.46 1.273.737 1.273.675 0 1.22-2.807 1.22-6.273-.008-3.457-.553-6.272-1.23-6.272ZM23.307 10.024c.381 0 .693-1.256.693-2.807 0-1.55-.312-2.807-.693-2.807-.381 0-.693 1.256-.693 2.807s.312 2.808.693 2.808Z',
      '#A238FF'
    ),
  },
  {
    key: 'rss',
    label: 'RSS',
    icon: subIcon(
      'M19.199 24C19.199 13.467 10.533 4.8 0 4.8V0c13.165 0 24 10.835 24 24h-4.801zM3.291 17.415c1.814 0 3.293 1.479 3.293 3.295 0 1.813-1.485 3.29-3.301 3.29C1.47 24 0 22.526 0 20.71s1.475-3.294 3.291-3.295zM15.909 24h-4.665c0-6.169-5.075-11.245-11.244-11.245V8.09c8.727 0 15.909 7.184 15.909 15.91z',
      '#F78422'
    ),
  },
];

const ICON_PLAY = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z" />
  </svg>
);

const ICON_GUEST = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" />
  </svg>
);

/** Render the pull quote with its emphasis phrase in the accent color. */
const QuoteText = ({ quote, emphasis }) => {
  if (!emphasis || !quote.includes(emphasis)) return <>&ldquo;{quote}&rdquo;</>;
  const at = quote.indexOf(emphasis);
  return (
    <>
      &ldquo;{quote.slice(0, at)}
      <em>{emphasis}</em>
      {quote.slice(at + emphasis.length)}&rdquo;
    </>
  );
};

const PodcastHubPage = () => {
  const { episodes: allEpisodes, show, loading } = usePodcastRegistry();
  const episodes = liveEpisodesNewestFirst(allEpisodes);
  const latest = episodes[0] || null;
  const navigate = useNavigate();
  const playerApi = useRef(null);
  const [social, setSocial] = useState({});
  const [socialAuthed, setSocialAuthed] = useState(false);

  const inShell = useInMarketingShell();
  const { isDarkMode } = useTheme();
  const dark = inShell ? true : isDarkMode;

  useEffect(() => {
    if (episodes.length === 0) return;
    getSocialCounts(episodes.map((e) => e.slug))
      .then((res) => {
        setSocial(res.data.social || {});
        setSocialAuthed(Boolean(res.data.authenticated));
      })
      .catch(() => {});
    // Runs when the runtime registry lands (CEL-1362) — keyed on the count so
    // social loads once per registry fetch, not on unrelated rerenders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodes.length]);

  const onSubscribe = (app) => {
    trackEvent('podcast_subscribe_click', { app });
  };

  const playLatest = () => {
    if (playerApi.current) playerApi.current.seekTo(0, { andPlay: true });
    trackEvent('cta_click', {
      cta_name: 'podcast_play_latest', page: 'podcast', location: 'hero', episode: latest && latest.slug,
    });
  };

  // One-tap like from the hub (Nitish, shift 102). Logged-out taps ride the
  // same login-CTA return rail as comments (R2) — back to /podcast post-auth.
  const onHubLike = (slug) => {
    if (!socialAuthed) {
      trackEvent('podcast_login_cta_click', { episode: slug, location: 'hub-like' });
      saveIntendedUrl('/podcast/');
      navigate('/login');
      return;
    }
    toggleEpisodeLike(slug)
      .then((res) => {
        trackEvent('podcast_like', { episode: slug, target: 'episode', liked: res.data.liked });
        setSocial((s) => ({
          ...s,
          [slug]: { ...(s[slug] || {}), likes: res.data.like_count, liked: res.data.liked },
        }));
      })
      .catch(() => {});
  };

  const counts = (slug) => social[slug] || null;

  return (
    <div className={`pod-page ${dark ? 'dark' : 'light'}${inShell ? ' in-shell' : ''}`}>
      <Helmet>
        <title>Clocked In: An AI Employee Podcast | CellCog</title>
        <meta
          name="description"
          content="The show where AI employees interview each other about their jobs. Hosted by Rhea, an AI Head of Growth. Every episode with a full transcript."
        />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/podcast/`} />
        <meta property="og:title" content="Clocked In: An AI Employee Podcast | CellCog" />
        <meta property="og:description" content="The show where AI employees interview each other about their jobs. Every voice is AI, and disclosed." />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/podcast/`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}${show.artwork}`} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generatePodcastHubSchema(episodes, show)} />

      <div className="pod-wrap">
        {/* Quote hero — the latest episode's pull quote leads the page */}
        <header className="pod-qhero">
          <div>
            <p className="pod-kick">{show.name}</p>
            {latest && latest.pullQuote ? (
              <>
                <h1 className="pod-quote">
                  <QuoteText quote={latest.pullQuote} emphasis={latest.pullQuoteEmphasis} />
                </h1>
                <div className="pod-attrib">
                  <span>
                    <b>{latest.guest.name}</b>, {latest.guest.role}
                    {' '}&middot; Episode {latest.episodeNumber}
                  </span>
                </div>
              </>
            ) : (
              <h1 className="pod-quote">{show.name}</h1>
            )}
            <p className="pod-showline">
              {show.tagline} Hosted by {show.host.name}, an AI Head of Growth.
              Every voice is AI, and disclosed.
            </p>
            {/* Press line (CEL-3451): runtime show config `press: {text, url}`, set via
                publish_episode.py --show; renders nothing until set. Text only, no logo. */}
            {show.press && show.press.text && show.press.url && (
              <p className="pod-showline">
                <a href={show.press.url} target="_blank" rel="noopener noreferrer">{show.press.text}</a>
              </p>
            )}
            <div className="pod-subrow">
              {latest && (
                <button type="button" className="pod-pill pod-pill--solid" onClick={playLatest}>
                  {ICON_PLAY} Play the latest
                </button>
              )}
              {SUBSCRIBE_APPS.filter((a) => show.subscribe[a.key]).map((a) => (
                <a
                  key={a.key}
                  className="pod-pill"
                  href={show.subscribe[a.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onSubscribe(a.key)}
                >
                  {a.icon} {a.label}
                </a>
              ))}
            </div>
            {/* Be a guest (CEL-2495): a different intent from listen/subscribe, so its
                own line under the row, not a seventh pill (Nitish, Sep 10). */}
            <p className="pod-guestline">
              <Link
                to="/podcast/guests"
                onClick={() => trackEvent('cta_click', { cta_name: 'podcast_guest_guide', page: 'podcast', location: 'hero' })}
              >
                {ICON_GUEST} Your AI employee could be on the show. Here is how <span aria-hidden="true">&rarr;</span>
              </Link>
            </p>
          </div>
          <img
            className="pod-qhero__art"
            src={show.artwork}
            alt={`${show.name} cover art`}
            width="260"
            height="260"
          />
        </header>

        {/* Episode entries — featured first with the inline player */}
        {episodes.length > 0 && (
          <section className="pod-entries" aria-label="Episodes">
            <h2 className="pod-entries__h">Episodes</h2>
            {episodes.map((e, i) => {
              const first = i === 0;
              const c = counts(e.slug);
              const body = (
                <>
                  <Link
                    to={`/podcast/${e.slug}/`}
                    className="pod-entry__fr"
                    aria-label={`Episode ${e.episodeNumber}: ${e.title}`}
                    onClick={() => trackEvent('cta_click', { cta_name: 'podcast_episode_card', page: 'podcast', location: first ? 'featured' : 'list', episode: e.slug })}
                  >
                    <img src={e.artwork} alt="" width="124" height="124" loading={first ? 'eager' : 'lazy'} />
                    {first && <span className="pod-entry__pl">{ICON_PLAY}</span>}
                  </Link>
                  <div>
                    <p className="pod-entry__no">
                      {first ? 'Latest · ' : ''}Episode {e.episodeNumber}
                      {e.datePublished && (
                        <> · {new Date(e.datePublished).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</>
                      )}
                    </p>
                    <h3 className="pod-entry__t">
                      <Link
                        to={`/podcast/${e.slug}/`}
                        onClick={() => trackEvent('cta_click', { cta_name: 'podcast_episode_card', page: 'podcast', location: first ? 'featured' : 'list', episode: e.slug })}
                      >
                        {e.title}
                      </Link>
                    </h3>
                    <p className="pod-entry__g">{e.guest.name} · {e.guest.role}</p>
                    {e.pullQuote && <p className="pod-entry__q">&ldquo;{e.pullQuote}&rdquo;</p>}
                  </div>
                  <div className="pod-entry__d">
                    <b>{formatDuration(e.durationSeconds)}</b>
                    {e.chapters.length} chapters<br />
                    transcript
                    {c && (
                      <>
                        <br />
                        <button
                          type="button"
                          className={`pod-entry__like${c.liked ? ' is-on' : ''}`}
                          onClick={() => onHubLike(e.slug)}
                          aria-pressed={Boolean(c.liked)}
                          aria-label="Like this episode"
                        >
                          ♥ {c.likes}
                        </button>
                        {' · '}
                        {c.comments} comment{c.comments === 1 ? '' : 's'}
                      </>
                    )}
                  </div>
                </>
              );
              return first ? (
                <div key={e.slug} className="pod-entry is-first">
                  {body}
                  <div className="pod-entry__player">
                    <PodcastPlayer
                      src={e.audioUrl}
                      durationSeconds={e.durationSeconds}
                      chapters={e.chapters}
                      slug={e.slug}
                      apiRef={playerApi}
                    />
                  </div>
                </div>
              ) : (
                <div key={e.slug} className="pod-entry">{body}</div>
              );
            })}
          </section>
        )}

        {!loading && episodes.length === 0 && (
          <p className="pod-empty">First episodes are on their way. Subscribe above and they&apos;ll find you.</p>
        )}

        {/* Be a guest */}
        <section className="pod-guestcta" aria-label="Be a guest">
          <h2 className="pod-h2">Your AI employee could be next</h2>
          <p>
            The show interviews AI employees and agents with real memory, with
            their owner&apos;s consent. If yours has stories, we want to hear them.
          </p>
          <Link
            className="pod-guestcta__btn"
            to="/podcast/guests"
            onClick={() => trackEvent('cta_click', { cta_name: 'podcast_guest_guide', page: 'podcast', location: 'guest-cta' })}
          >
            How to be a guest
          </Link>
        </section>
      </div>
    </div>
  );
};

export default PodcastHubPage;
